import { Card, DotSeparatedList, Text } from "@clipboard-health/ui-components";
import { CardContent, Stack } from "@mui/material";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { TimeRangeLabel } from "@src/appV2/redesign/DateTime/TimeRangeLabel";
import { InstantPayPill } from "@src/appV2/redesign/Shift/InstantPayPill";
import { TimeSlotIcon } from "@src/appV2/redesign/Shift/TimeSlotIcon";
import { formatDurationWithBreakParts } from "@src/appV2/redesign/utils/formatDurationWithBreakParts";
import { type ShiftPeriod, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, parseISO } from "date-fns";

interface ShiftOverviewCardProps {
  shiftPeriod: ShiftPeriod;
  shiftStartIso: string;
  shiftEndIso: string;
  qualificationName: string;
  isInstantPay: boolean;
  hourlyPay: string;
  totalPay: string;
  workplaceTimezone: string;
  breakDurationInMinutes?: number;
  requiresBreak?: boolean;
}

export function ShiftOverviewCard(props: ShiftOverviewCardProps) {
  const {
    shiftPeriod,
    shiftStartIso,
    shiftEndIso,
    qualificationName,
    isInstantPay,
    hourlyPay,
    totalPay,
    workplaceTimezone,
    breakDurationInMinutes = 0,
    requiresBreak = false,
  } = props;
  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    qualificationName,
  ]);
  const qualificationDisplayLabel =
    qualificationNameToDisplayLabelMap.get(qualificationName) ?? qualificationName;

  const totalDurationInHours =
    differenceInMinutes(parseISO(shiftEndIso), parseISO(shiftStartIso)) / 60;
  const shiftDurationParts = formatDurationWithBreakParts({
    totalDurationInHours,
    breakDurationInMinutes,
    requiresBreak,
  });
  return (
    <Card sx={{ flexGrow: 1 }}>
      <CardContent sx={{ padding: 7 }}>
        <Stack direction="row" spacing={4} alignItems="flex-start">
          <TimeSlotIcon
            timeSlot={shiftPeriodToTimeSlot[shiftPeriod]}
            size="large"
            variant="outlined"
          />
          <Stack direction="column" spacing={2} alignItems="flex-start">
            <TimeRangeLabel
              variant="h5"
              dateRange={{
                startDate: parseISO(shiftStartIso),
                endDate: parseISO(shiftEndIso),
              }}
              timezone={workplaceTimezone}
            />
            <Stack direction="column">
              <DotSeparatedList>
                <Text variant="body2">{qualificationDisplayLabel}</Text>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Text variant="body1">{shiftDurationParts.duration}</Text>
                  <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                    {shiftDurationParts.breakInfo}
                  </Text>
                </Stack>
              </DotSeparatedList>
              <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between">
                <DotSeparatedList>
                  <Text variant="body2">{hourlyPay} / hr</Text>
                  <Text variant="body2">{totalPay} total</Text>
                </DotSeparatedList>
                {isInstantPay ? <InstantPayPill /> : null}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
